import { computeFusedPath } from "./utils/fused-path.js";
import { vec3 } from "gl-matrix";
//#region ../src/utils/user-heading.ts
/**
* True-north user-heading kernel.
*
* Turns the latest AR camera rotation into an absolute compass bearing (degrees
* clockwise from geographic North) so the live/replay map overlay can draw a
* thin view-direction line from the user-position dot. See Finding 2 of
* gps-plus-slam/GpsPlusSlamJs_Docs/docs/2026-06-28-1822-map-rings-transparency-and-view-direction-user-feedback.md.
*
* Why this lives in the app-framework (NOT the library's `orientation-heading.ts`):
* that library module is the MAGNETIC path — its bearings come from the
* AbsoluteOrientationSensor's device→ENU quaternion and are explicitly magnetic
* (they need WMM declination correction). Finding 2's locked source is the
* GPS+SLAM **alignment matrix → true geographic north**, which rejects the
* magnetic sensor. So the math belongs here, alongside `fused-path.ts`, which
* already converts AR odometry through the same alignment matrix.
*
* Frame algebra (pinned by `user-heading.test.ts`):
* - State stores odometry rotations as NUE quaternions
*   (`webxrQuaternionToNUE(odomRotation)` in the library reducer). NUE means
*   X=North, Y=Up, Z=East.
* - A WebXR camera looks down its own −Z. The basis-change `webxrToNUE` maps
*   that local forward `[0,0,-1]` to `[1,0,0]`, so the camera-forward basis
*   vector in the NUE-AR frame is **[1, 0, 0]** (an identity camera looks North).
* - The alignment matrix maps AR-NUE → world NUE metres (same matrix
*   `computeFusedPath` applies to positions). Rotating the forward direction by
*   it yields a world-NUE direction; the geographic bearing is
*   `atan2(East, North)`.
*
* We rotate the *direction* and read the bearing directly rather than
* round-tripping two points through `calcGpsCoords`: lat/lon scale North and
* East by different metres-per-degree, so a naive GPS-coord bearing would be
* anisotropically distorted. The aligned NUE metres already carry the true
* bearing, so `atan2(East, North)` is both simpler and undistorted.
*/
/** Camera-forward basis vector in the NUE-AR frame: `webxrToNUE([0,0,-1]) = [1,0,0]`. */
const NUE_CAMERA_FORWARD = [
	1,
	0,
	0
];
/**
* Minimum horizontal fraction of the (unit) forward direction below which the
* heading is treated as undefined. Because `horiz/len = sin(angle from
* vertical)`, `horiz/len < GUARD` means the camera points within
* `asin(0.08) ≈ 4.6°` of straight up/down (equivalently, pitched more than ~85°
* from horizontal), where a 2D map bearing is meaningless and jittery. Mirrors
* the rationale of the library's `VERTICAL_GUARD` (0.08).
*/
const VERTICAL_GUARD = .08;
const RAD_TO_DEG = 180 / Math.PI;
const _forward = vec3.create();
const _origin = vec3.create();
const _tip = vec3.create();
const _dir = vec3.create();
/**
* Compute the user's absolute view-direction bearing in degrees clockwise from
* geographic North, in `[0, 360)`, or `null` when undefined.
*
* Returns `null` when there is no rotation yet, no alignment matrix yet, or the
* camera points near-vertically (see {@link VERTICAL_GUARD}). Position is NOT
* required — the bearing is a pure direction; the consumer only draws the line
* when it also has a user-position dot to anchor it to.
*/
function computeUserHeadingDeg(input) {
	const { odometryRotation, alignmentMatrix } = input;
	if (!odometryRotation || !alignmentMatrix) return null;
	vec3.transformQuat(_forward, NUE_CAMERA_FORWARD, odometryRotation);
	vec3.set(_origin, 0, 0, 0);
	vec3.transformMat4(_origin, _origin, alignmentMatrix);
	vec3.transformMat4(_tip, _forward, alignmentMatrix);
	vec3.subtract(_dir, _tip, _origin);
	const north = _dir[0];
	const east = _dir[2];
	const len = Math.hypot(_dir[0], _dir[1], _dir[2]);
	const horiz = Math.hypot(north, east);
	if (!Number.isFinite(len) || !Number.isFinite(horiz) || len < 1e-9 || horiz / len < VERTICAL_GUARD) return null;
	return (Math.atan2(east, north) * RAD_TO_DEG % 360 + 360) % 360;
}
//#endregion
//#region ../src/visualization/map-data.ts
/**
* Build a {@link MapData} snapshot from store-derived inputs.
*
* Pure and free of Leaflet/Three dependencies. The fused path is always
* derived via {@link computeFusedPath} (D2); when the matrix or zero reference
* is missing it is an empty array.
*
* @param input - Optional trajectory slices (see {@link MapDataInput}).
* @returns A fully-resolved {@link MapData} with all arrays defensively copied.
*/
function buildMapData(input) {
	const rawGpsPath = input.rawGpsPath ? [...input.rawGpsPath] : [];
	const alignmentSnapshots = input.alignmentSnapshots ? [...input.alignmentSnapshots] : [];
	const fusedPath = computeFusedPath({
		odometryPositions: input.odometryPositions ?? [],
		alignmentMatrix: input.alignmentMatrix ?? null,
		zeroRef: input.zeroRef ?? null
	});
	const lastFused = fusedPath[fusedPath.length - 1];
	const lastRaw = rawGpsPath[rawGpsPath.length - 1];
	const userPosition = input.userPosition !== void 0 ? input.userPosition : lastFused ? {
		lat: lastFused.lat,
		lng: lastFused.lng
	} : lastRaw ? {
		lat: lastRaw.lat,
		lng: lastRaw.lng
	} : null;
	const rotations = input.odometryRotations ?? [];
	return {
		userPosition,
		rawGpsPath,
		fusedPath,
		alignmentSnapshots,
		userHeadingDeg: computeUserHeadingDeg({
			odometryRotation: rotations[rotations.length - 1] ?? null,
			alignmentMatrix: input.alignmentMatrix ?? null
		})
	};
}
//#endregion
export { buildMapData as t };
